import React, { useCallback, useMemo, forwardRef } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import filter from 'lodash/filter'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import Grid from '@material-ui/core/Grid'

import { reloadSchema } from '@galo-monitor/forms'
import userSchema from '@galo-monitor/forms/schemas/farm/user.schema'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { FarmUserActions } from '@galo-monitor/stores/farmUser'
import Form from '@galo-monitor/web-components/Form'
import InputEmail from '@galo-monitor/web-components/InputEmail'
import InputPhone from '@galo-monitor/web-components/InputPhone'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'

import { Container } from './styles'

const UserForm = forwardRef((props, formRef) => {
  const { user, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const { data: userData } = useGaloMonitorApi(user.id ? `/farms/users/${ user.id }` : null)
  const { data: properties, isValidating } = useGaloMonitorApi('/farms/properties')
  const { data: profiles } = useGaloMonitorApi('/farms/roles')

  const propertyOptions = useMemo(
    () => map(
      filter([
        {
          name: t('all properties'),
          id: 'all'
        },
        ...properties
      ], property => property.name),
      property => ({
        label: property.name,
        value: property.id.toString()
      })
    ),
    [properties, t]
  )

  const profileOptions = useMemo(
    () => map(profiles, profile => ({
      label: profile.name,
      value: profile.name
    })),
    [profiles]
  )

  const userPropertiesDefaultValue = useMemo(
    () => {
      if (!isEmpty(userData)) {
        return map(userData?.propertiesAccess, property => property.id.toString())
      }

      return []
    },
    [userData]
  )

  const revalidateSchema = useCallback(
    () => reloadSchema(formRef, true),
    [formRef]
  )

  const handleSelectAllFarms = useCallback(evt => {
    if (evt.target.value.includes('all'))
      formRef.current.setFieldValue(
        'properties_access',
        map(
          filter(propertyOptions, propOpt => propOpt.value !== 'all'),
          filteredOpt => filteredOpt.value
        )
      )
  }, [formRef, propertyOptions])

  const handleSubmit = useCallback(
    (data) => {
      setLoading(true)
      dispatch(FarmUserActions.saveFarmUser(
        {
          ...user,
          ...data,
          farm_role_id: find(profiles, profile => profile.name === data.farm_role_id).id,
          properties_access: map(data.properties_access, propertyId => ({ id: +propertyId }))
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('user', { howMany: 1 }),
            action: toLower(t(isEmpty(user) ? 'created' : 'updated', { gender: 'male', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [dispatch, onError, onSuccess, profiles, setLoading, snackbar, t, user]
  )

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ userSchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <Grid container spacing={ 2 }>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputText
              name="name"
              label={ t('name') }
              defaultValue={ user.name }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputPhone
              name="phone"
              label={ t('phone') }
              defaultValue={ user.phone }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputEmail
              name="email"
              label={ t('email') }
              defaultValue={ user.email }
              onChange={ revalidateSchema }
              disabled={ loading || !!user.id }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputEmail
              name="email_confirmation"
              label={ t('confirm email') }
              defaultValue={ user.email }
              onChange={ revalidateSchema }
              disabled={ loading || !!user.id }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputSelect
              name="farm_role_id"
              label={ t('role', { howMany: 1 }) }
              defaultValue={ user.role?.toString() ?? '' }
              options={ profileOptions }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputText
              name="farm_role_position"
              label={ t('role position', { howMany: 1 }) }
              defaultValue={ user.rolePosition }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } style={ { marginTop: 15 } }>
            <InputSelect
              name="properties_access"
              label={ t('property', { howMany: 2 }) }
              defaultValue={ userPropertiesDefaultValue }
              onChange={ handleSelectAllFarms }
              options={ propertyOptions }
              disabled={ loading }
              loading={ isValidating }
              multiple
              fullWidth
            />
          </Grid>
        </Grid>
      </Container>
    </Form>
  )
})

UserForm.propTypes = {
  user: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

UserForm.defaultProps = {
  user: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default UserForm
